import { objectsMatch } from './Utils'
import { ValidTimeZone } from './Utils/Time/TZ'
import { isNonNaNNumber } from './Validation/validationCore'
import { ExtendedMetadata, ValidBusinessObject } from './datastoreProxy'
import { Position as TurfPosition } from 'geojson'

enum Heading {
  N,
  NNE,
  NE,
  ENE,
  E,
  ESE,
  SE,
  SSE,
  S,
  SSW,
  SW,
  WSW,
  W,
  WNW,
  NW,
  NNW, // 16
}

export type TimedPosition = Position & {
  timestamp: number
  heading?: number
  speed?: number
}

export type Position = {
  lat: number
  lng: number
  elevation?: number
  DOP?: number
  age?: number
  id?: string
}

export const isValidLatitude = (lat: any) => isNonNaNNumber(lat) && Math.abs(lat) <= 90
export const isValidLongitude = (lng: any) => isNonNaNNumber(lng) && Math.abs(lng) <= 180

export const isPosition = (possiblePosition: { [k: string]: any } | undefined): possiblePosition is Position => {
  return !!possiblePosition && isValidLatitude(possiblePosition.lat) && isValidLongitude(possiblePosition.lng)
}

export const isTurfPosition = (pos: any): pos is TurfPosition => !!pos && pos instanceof Array && isValidLongitude(pos.at(0)) && isValidLongitude(pos.at(1))

export const isPositionArray = (possible: any) => {
  return possible instanceof Array && possible.every(isPosition)
}

/** IOTV Device Specific GPS field spec */
export type GPS_Data = {
  speed: number
  heading: Heading
  age: number
  DOP: number
  InitMode?: number
} & Position

/** IOTV Device Specific GPS field spec */
export type Static_GPS_Data = {
  age: number
  DOP: number
} & Position

/** IOTV Device Specific GPS field spec */
export const isGPS_Data = (possiblePosition: { [k: string]: any }): possiblePosition is GPS_Data =>
  isNonNaNNumber(possiblePosition.DOP) && isNonNaNNumber(possiblePosition.age) && isNonNaNNumber(possiblePosition.heading) && isPosition(possiblePosition)
export type Bounds = { ne: Position; sw: Position }

export const isBounds = (ob: any): ob is Bounds => {
  const { sw, ne } = (ob ?? {}) as Bounds
  return [sw, ne].every(isPosition)
}

export const hasBounds = (ob: any): ob is { bbox: Bounds } => !!ob && isBounds(ob.bbox)

type LocatableByPosition = { location: Position }
export const isLocatableByPosition = (ob: Record<string, any>): ob is LocatableByPosition => {
  return ob && isPosition((ob as LocatableByPosition).location)
}

type LocatableByBounds = { points: Position[] }
export const isLocatableByBounds = (ob: Record<string, any>): ob is LocatableByBounds => {
  return ob && (ob as LocatableByBounds).points?.length > 2 && (ob as LocatableByBounds).points?.every(isPosition)
}

export interface IPath {
  points: Position[]
  bbox?: Bounds
}

export interface IGeofence {
  points: Position[]
}

export interface IPlace {
  location: Position
}

/** currently this will return true for polygons, as it is possible that someone may want to describe a closing path */
export const isIPath = (item: any): item is IPath =>
  item && item.points instanceof Array && item.points.length > 1 && (item.points as any[]).every((point) => isPosition(point))

/** returns true if item has points which form at least a triangle and where first and last points are the same */
export const isIGeofence = (item: any): item is IGeofence =>
  item &&
  item.points instanceof Array &&
  item.points.length > 2 &&
  (item.points as any[]).every((point) => isPosition(point)) &&
  /** 2024 06 possible data breaking addition. One of Turf or Polygons requires the first and last points to match */
  objectsMatch(item.points[0], item.points[item.points.length - 1])

export const isIPlace = (item: any): item is IPlace => item && isPosition(item.location) && !isIPath(item)

export const isValidGeoObject = (item: any): item is ValidGeoObject =>
  item && (isIGeofence(item) || isIPath(item) || isIPlace(item) || isPosition(item.points?.at(0)))

/** closes unclosed points where x.points is and array of Positions */
export const makeIPathToIGeofence = (item: any) => {
  if (isIPath(item)) {
    if (!objectsMatch(item.points[0], item.points[item.points.length - 1])) {
      item.points.push(item.points[0])
    }
    return item as IGeofence
  } else return null
}

export interface IGeoModelCommon {
  location?: Position
  locationSysUpdateable?: boolean
  cellLevel?: number
  tz?: ValidTimeZone
}

/** Eg Areas or Places, where a bounding box can be derived from either the perimeter or the expanded bounds of a location */
export type ValidGeoObject<T extends Record<string, any> = Record<string, any>> = ValidBusinessObject & (IGeofence & IGeoModelCommon) & { data?: T }

export type ItemGeneratedGeoObjectKey = 'generatedFrom'
export const itemGeneratedGeoObjectKey: ItemGeneratedGeoObjectKey = 'generatedFrom'

export type ItemGeneratedGeoObjectCellSizeKey = 'cellSizeMtrs'
export const itemGeneratedGeoObjectCellSizeKey = 'cellSizeMtrs'

export type ItemGeneratedGeoObjectMetadata = {
  [itemGeneratedGeoObjectKey]: string
  /** A string representation of lenght of cells in meters in a square grid  */
  [itemGeneratedGeoObjectCellSizeKey]: string
}

/** extended metadata for ValidGeoObject with generatedFrom as the sk of the item this has been derived from, a proxy for parent */
export type GeneratedGeoObject<T extends Record<string, any> = Record<string, any>> = ExtendedMetadata<ValidGeoObject<T>, ItemGeneratedGeoObjectMetadata>

export const isItemGeneratedGeoObject = (x: any): x is GeneratedGeoObject =>
  isValidGeoObject(x) &&
  typeof (x as GeneratedGeoObject).metadata?.[itemGeneratedGeoObjectKey] === 'string' &&
  typeof (x as GeneratedGeoObject).metadata?.[itemGeneratedGeoObjectCellSizeKey] === 'string'

export type GeneratedGridCell<T extends Record<string, any> = Record<string, any>> = ExtendedMetadata<
  ValidGeoObject<T> & IGeofence,
  ItemGeneratedGeoObjectMetadata
>
export const isItemGeneratedGridCell = (x: any): x is GeneratedGridCell => isItemGeneratedGeoObject(x) && isIGeofence(x)

export type GeoGrid = ValidBusinessObject & {
  name: string
  generatedFrom: string | null
  cells: GeneratedGeoObject[]
}

export const isGeoGrid = (x: any): x is GeoGrid => !!x && (x as GeoGrid).cells?.every((cell) => isItemGeneratedGridCell(cell))

export type AreaGridMap = { [k: string]: GeoGrid }
export type ExtendedAreaWithGridMetadata = { grids: AreaGridMap; gridsHash?: string }
export type AreaWithGrids = ExtendedMetadata<ValidGeoObject & IGeofence, ExtendedAreaWithGridMetadata>

export const isAreaWithGrids = (x: any): x is AreaWithGrids => {
  return (
    isIGeofence(x) && !!(x as AreaWithGrids).metadata?.grids && Object.values((x as AreaWithGrids).metadata?.grids).every((grid) => grid.cells instanceof Array)
  )
}
